import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { ArrowLeft, Loader2, Save, Sparkles, Search, AlertCircle, Edit, GraduationCap, Upload, FileText, X, Target, Briefcase, Users, DollarSign, ShieldAlert } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import SuccessCoachingQuestionsDialog from '../components/SuccessCoachingQuestionsDialog';
import { filterCaseNoteTemplates } from '../utils/caseNoteTemplates';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CaseNoteFormPage = () => {
  const navigate = useNavigate();
  const { noteId } = useParams(); // For edit mode
  const [searchParams] = useSearchParams();
  const preselectedCase = searchParams.get('case');
  const preselectedParticipant = searchParams.get('participant');
  
  const isEditMode = !!noteId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiAssisting, setAiAssisting] = useState(false);
  const [cases, setCases] = useState([]);
  const [participantCases, setParticipantCases] = useState([]); // Cases for dropdown when participant is preselected
  const [caseSearch, setCaseSearch] = useState('');
  const [selectedCase, setSelectedCase] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [aiDraft, setAiDraft] = useState('');
  const [aiResponse, setAiResponse] = useState(null); // Full AI response with follow-up questions
  const [aiInput, setAiInput] = useState('');
  const [originalNote, setOriginalNote] = useState(null);
  const [participant, setParticipant] = useState(null); // Participant info when coming from participant notes tab
  const [noteTypes, setNoteTypes] = useState([]); // Dynamic note types from API
  
  // Pathways Module Selection state
  const [showModuleDialog, setShowModuleDialog] = useState(false);
  const [selectedModule, setSelectedModule] = useState('');
  const [pendingTemplateId, setPendingTemplateId] = useState(null);
  
  const PATHWAYS_MODULES = [
    "Building a Foundation",
    "Developing Self-Awareness How to Sell Yourself",
    "Resume Writing",
    "Finding Work & The Hidden Job Market",
    "Communication Skills",
    "Job Interview Skills",
    "Time Management",
    "Starting Work and Staying Employed"
  ];
  
  // Supportive Service Assistance Selection state
  const [showServiceTypeDialog, setShowServiceTypeDialog] = useState(false);
  const [selectedServiceType, setSelectedServiceType] = useState('');
  const [supportiveServiceTypes, setSupportiveServiceTypes] = useState([]);
  
  // Document upload state
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [documentNames, setDocumentNames] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Targeted Outcomes validation state
  const [targetedOutcomesStatus, setTargetedOutcomesStatus] = useState(null);
  const [showTargetedOutcomesAlert, setShowTargetedOutcomesAlert] = useState(false);
  
  // Employment Outcome alert state
  const [showEmploymentOutcomeAlert, setShowEmploymentOutcomeAlert] = useState(false);
  const [employmentAlertMessage, setEmploymentAlertMessage] = useState('');
  
  // Highlighted notes contradiction check state
  const [checkingContradiction, setCheckingContradiction] = useState(false);
  const [contradictionResult, setContradictionResult] = useState(null);
  const [showContradictionAlert, setShowContradictionAlert] = useState(false);
  
  // Enrollment Summary popup state (for {{enrollment_summary}} merge field)
  const [showEnrollmentSummaryPopup, setShowEnrollmentSummaryPopup] = useState(false);
  const [enrollmentSummaryLoading, setEnrollmentSummaryLoading] = useState(false);
  const [enrollmentSummaryData, setEnrollmentSummaryData] = useState({
    who_lives_in_household: '',
    household_income_source: '',
    has_criminal_background: false,
    criminal_background_details: ''
  });
  const [pendingContentWithEnrollmentSummary, setPendingContentWithEnrollmentSummary] = useState('');
  
  // Document type options
  const DOCUMENT_TYPE_OPTIONS = [
    "Resume",
    "Certification",
    "Government ID",
    "Job Offer Letter",
    "Training Certificate",
    "Employment Verification",
    "Pay Stub",
    "Signed Form",
    "Other"
  ];

  const [formData, setFormData] = useState({
    case_id: preselectedCase || '',
    subject: '',
    template_id: '',
    date_of_service: new Date().toISOString().split('T')[0],
    content: '',
    note_type: 'progress',
    is_sensitive: false,
    structured_data: {}
  });

  useEffect(() => {
    fetchTemplates();
    fetchNoteTypes();
    if (isEditMode) {
      fetchNote(noteId);
    } else if (preselectedCase) {
      fetchCase(preselectedCase);
    } else if (preselectedParticipant) {
      // Coming from participant's Notes tab - fetch participant's cases
      fetchParticipantCases(preselectedParticipant);
    }
  }, [noteId, preselectedCase, preselectedParticipant]);
  
  // Success Coaching weekly program: questions pop-up for "Success Coaching Encounter" notes
  const [scWeekInfo, setScWeekInfo] = useState(null);
  const [showScDialog, setShowScDialog] = useState(false);
  const [scCheckedKey, setScCheckedKey] = useState('');
  const [scSession, setScSession] = useState(null);
  const SC_SUBJECT = 'success coaching encounter';
  const selectedTemplateSubject = (templates.find(t => t.id === formData.template_id)?.subject || '').trim().toLowerCase();
  const isSuccessCoachingNote = (formData.subject || '').trim().toLowerCase() === SC_SUBJECT || (!!formData.template_id && selectedTemplateSubject === SC_SUBJECT);

  useEffect(() => {
    if (isEditMode || !formData.case_id || !formData.template_id || !isSuccessCoachingNote) return;
    const key = `${formData.case_id}-${formData.template_id}`;
    if (scCheckedKey === key) return;
    setScCheckedKey(key);
    setScSession(null);
    axios.get(`${API}/cases/${formData.case_id}/success-coaching/current-week`)
      .then(({ data }) => {
        if (data.completed) {
          toast.info('This participant has completed the 12-week Success Coaching program. The weekly questions and messages are not used.');
          return;
        }
        setScWeekInfo(data);
        if (data.week?.questions?.length) {
          setShowScDialog(true);
        } else {
          setScSession({ week_number: data.week_number, win_wall: '' });
        }
      })
      .catch(() => toast.error('Could not load this week\'s Success Coaching questions'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.case_id, formData.template_id, isSuccessCoachingNote, isEditMode]);

  const handleScComplete = (result) => {
    setShowScDialog(false);
    setFormData(prev => ({
      ...prev,
      content: (prev.content || '')
        .split('{{personalized_encounter_summary}}').join(result.personalized_encounter_summary || '')
        .split('{{win_wall}}').join(result.win_wall || '')
        .split('{{success_coaching_week_topic}}').join(result.week_topic || '')
    }));
    setScSession({ week_number: result.week_number, win_wall: result.win_wall || '' });
    toast.success('Personalized Encounter Summary added to the note');
  };

  const handleScSkip = () => {
    setShowScDialog(false);
    if (scWeekInfo?.week_number) setScSession({ week_number: scWeekInfo.week_number, win_wall: '' });
  };

  const sendSuccessCoachingMessages = async (noteId) => {
    if (!scSession || !isSuccessCoachingNote) return;
    try {
      const { data } = await axios.post(`${API}/cases/${formData.case_id}/success-coaching/send-messages`, {
        week_number: scSession.week_number, win_wall: scSession.win_wall, note_id: noteId
      });
      (data.results || []).forEach(r => {
        const label = r.channel === 'email' ? 'Email' : 'Text';
        if (r.status === 'sent') toast.success(`Success Coaching ${label} sent to participant`);
        else toast.warning(`Success Coaching ${label} not sent: ${r.reason || r.status}`);
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send Success Coaching messages');
    }
  };

  // Watch for enrollment_summary placeholder in content after template is loaded
  useEffect(() => {
    // Only check when there's a case selected and content contains the placeholder
    // Also ensure we're not already showing the popup and no pending content
    if (formData.case_id && 
        formData.content && 
        !showEnrollmentSummaryPopup && 
        !pendingContentWithEnrollmentSummary &&
        (formData.content.includes('{{enrollment_summary}}') || formData.content.includes('{enrollment_summary}'))) {
      checkForEnrollmentSummaryPlaceholder(formData.content);
    }
  }, [formData.content, formData.case_id]);

  const fetchNoteTypes = async () => {
    try {
      const response = await axios.get(`${API}/dropdowns/note_types`);
      if (response.data.items && response.data.items.length > 0) {
        setNoteTypes(response.data.items);
      } else {
        // Fallback to default note types if API returns empty
        setNoteTypes([
          { value: 'progress', label: 'Progress Note' },
          { value: 'intake', label: 'Intake Assessment' },
          { value: 'crisis', label: 'Crisis Intervention' },
          { value: 'follow_up', label: 'Follow-up' },
          { value: 'referral', label: 'Referral' },
        ]);
      }
    } catch (error) {
      console.error('Failed to fetch note types:', error);
      // Fallback to default note types
      setNoteTypes([
        { value: 'progress', label: 'Progress Note' },
        { value: 'intake', label: 'Intake Assessment' },
        { value: 'crisis', label: 'Crisis Intervention' },
        { value: 'follow_up', label: 'Follow-up' },
        { value: 'referral', label: 'Referral' },
      ]);
    }
  };

  const fetchParticipantCases = async (participantId) => {
    setLoading(true);
    try {
      // Fetch participant info
      const participantRes = await axios.get(`${API}/participants/${participantId}`);
      setParticipant(participantRes.data);
      
      // Fetch participant's cases
      const casesRes = await axios.get(`${API}/participants/${participantId}/cases`);
      const openCases = (casesRes.data.items || []).filter(c => c.status === 'open');
      setParticipantCases(openCases);
      
      // If only one case, auto-select it
      if (openCases.length === 1) {
        setSelectedCase(openCases[0]);
        setFormData(prev => ({ ...prev, case_id: openCases[0].id }));
      }
    } catch (error) {
      console.error('Failed to fetch participant cases:', error);
      toast.error('Failed to load participant cases');
    } finally {
      setLoading(false);
    }
  };

  const fetchNote = async (id) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/case-notes/${id}`);
      const note = response.data;
      setOriginalNote(note);
      setFormData({
        case_id: note.case_id,
        subject: note.subject || '',
        template_id: note.template_id || '',
        date_of_service: note.date_of_service,
        content: note.content,
        note_type: note.note_type,
        is_sensitive: note.is_sensitive || false,
        structured_data: note.structured_data || {}
      });
      // Fetch the case for context
      await fetchCase(note.case_id);
    } catch (error) {
      toast.error('Failed to load note');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/case-note-templates`);
      // Sort templates alphabetically by name (A-Z)
      const sortedTemplates = [...response.data].sort((a, b) => 
        (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
      );
      setTemplates(sortedTemplates);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  // Filter templates based on selected case type
  const getFilteredTemplates = () => filterCaseNoteTemplates(templates, selectedCase);

  const fetchCase = async (id) => {
    try {
      const response = await axios.get(`${API}/cases/${id}`);
      setSelectedCase(response.data);
      setFormData(prev => ({ ...prev, case_id: id }));
      // Check targeted outcomes status for this case
      await checkTargetedOutcomesStatus(id);
    } catch (error) {
      console.error('Failed to fetch case:', error);
    }
  };
  
  const checkTargetedOutcomesStatus = async (caseId) => {
    try {
      const response = await axios.get(`${API}/cases/${caseId}/targeted-outcomes-status`);
      setTargetedOutcomesStatus(response.data);
      
      // Show alert if SNAP E&T case with incomplete targeted outcomes (and user can't bypass)
      if (response.data.is_snap_et_case && 
          !response.data.is_complete && 
          !response.data.can_bypass) {
        setShowTargetedOutcomesAlert(true);
      } else {
        setShowTargetedOutcomesAlert(false);
      }
    } catch (error) {
      console.error('Failed to check targeted outcomes status:', error);
      setTargetedOutcomesStatus(null);
    }
  };

  const searchCases = async (query) => {
    if (!query || query.length < 2) {
      setCases([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/search?q=${query}&entity_type=cases`);
      setCases(response.data.cases || []);
    } catch (error) {
      console.error('Failed to search cases:', error);
    }
  };

  const handleCaseSelect = async (caseItem) => {
    const fullCase = await axios.get(`${API}/cases/${caseItem.id}`);
    setSelectedCase(fullCase.data);
    setFormData(prev => ({ ...prev, case_id: caseItem.id }));
    setCases([]);
    setCaseSearch('');
    
    // Check targeted outcomes status for this case
    await checkTargetedOutcomesStatus(caseItem.id);
    
    // If a template with body is already selected, re-populate merge fields
    if (selectedTemplate && selectedTemplate.body) {
      try {
        const response = await axios.post(`${API}/case-note-templates/populate`, {
          case_id: caseItem.id,
          body: selectedTemplate.body
        });
        
        if (response.data.populated_body) {
          setFormData(prev => ({ ...prev, content: response.data.populated_body }));
          toast.success('Template updated with participant data');
        }
      } catch (error) {
        console.error('Failed to populate template:', error);
      }
    }
  };

  const handleTemplateSelect = async (templateId) => {
    const template = templates.find(t => t.id === templateId);
    
    // Check if this is the Pathways module completion template
    if (template && (template.id === 'tpl-pathways-module-completion' || 
        template.name === 'Pathways to Employability Module Completion' ||
        template.requires_module_selection)) {
      // Show module selection dialog before proceeding
      setPendingTemplateId(templateId);
      setSelectedModule('');
      setShowModuleDialog(true);
      return;
    }
    
    // Check if this is the Supportive Service Assistance parent template
    if (template && (template.id === 'tpl-supportive-service-assistance' || 
        template.name === 'Supportive Service Assistance' ||
        template.requires_service_type_selection)) {
      // Fetch child templates (supportive service types)
      try {
        const response = await axios.get(`${API}/case-note-templates/tpl-supportive-service-assistance/child-templates`);
        setSupportiveServiceTypes(response.data.child_templates || []);
      } catch (error) {
        console.error('Failed to fetch supportive service types:', error);
        // Fallback to default types
        setSupportiveServiceTypes([
          { id: 'tpl-ss-background-check', name: 'Background Check', service_type: 'Background Check' },
          { id: 'tpl-ss-drug-testing', name: 'Drug Testing', service_type: 'Drug Testing' },
          { id: 'tpl-ss-flu-shot', name: 'Flu Shot', service_type: 'Flu Shot' },
          { id: 'tpl-ss-internet-service', name: 'Internet Service Assistance', service_type: 'Internet Service Assistance' },
          { id: 'tpl-ss-tb-skin-testing', name: 'TB-Skin Testing', service_type: 'TB-Skin Testing' },
          { id: 'tpl-ss-transportation', name: 'Transportation', service_type: 'Transportation' },
          { id: 'tpl-ss-tuition-assistance', name: 'Tuition Assistance', service_type: 'Tuition Assistance' },
          { id: 'tpl-ss-uniform-assistance', name: 'Uniform Assistance', service_type: 'Uniform Assistance' }
        ]);
      }
      // Show service type selection dialog
      setPendingTemplateId(templateId);
      setSelectedServiceType('');
      setShowServiceTypeDialog(true);
      return;
    }
    
    // Continue with normal template selection
    await applyTemplate(templateId);
  };
  
  const applyTemplate = async (templateId, moduleOverride = null) => {
    const template = templates.find(t => t.id === templateId);
    setSelectedTemplate(template);
    setFormData(prev => ({ ...prev, template_id: templateId }));
    
    // Pre-fill subject from template if available
    if (template && template.name && !formData.subject) {
      setFormData(prev => ({ ...prev, subject: template.name }));
    }
    
    // Set note type from template's default_note_type if available
    if (template && template.default_note_type) {
      setFormData(prev => ({ ...prev, note_type: template.default_note_type }));
    }
    
    // Get the case_id from formData or selectedCase
    const caseId = formData.case_id || selectedCase?.id;
    
    // If template has a body and we have a case selected, populate merge fields
    if (template && template.body && caseId) {
      try {
        const response = await axios.post(`${API}/case-note-templates/populate`, {
          case_id: caseId,
          body: template.body
        });
        
        if (response.data.populated_body) {
          let populatedContent = response.data.populated_body;
          
          // If a module was selected, replace the module placeholder
          if (moduleOverride) {
            populatedContent = populatedContent.replace('{{pathways_module}}', moduleOverride);
            // Store the selected module in structured_data
            setFormData(prev => ({ 
              ...prev, 
              content: populatedContent,
              structured_data: { ...prev.structured_data, pathways_module: moduleOverride }
            }));
          } else {
            setFormData(prev => ({ ...prev, content: populatedContent }));
          }
          toast.success('Template loaded with participant data');
        }
      } catch (error) {
        console.error('Failed to populate template:', error);
        // Fall back to raw template body if population fails
        if (template.body) {
          let rawContent = template.body;
          if (moduleOverride) {
            rawContent = rawContent.replace('{{pathways_module}}', moduleOverride);
            setFormData(prev => ({ 
              ...prev, 
              content: rawContent,
              structured_data: { ...prev.structured_data, pathways_module: moduleOverride }
            }));
          } else {
            setFormData(prev => ({ ...prev, content: rawContent }));
          }
          toast.info('Template loaded (merge fields not populated)');
        }
      }
    } else if (template && template.body) {
      // No case selected yet, just load the raw template
      let rawContent = template.body;
      if (moduleOverride) {
        rawContent = rawContent.replace('{{pathways_module}}', moduleOverride);
        setFormData(prev => ({ 
          ...prev, 
          content: rawContent,
          structured_data: { ...prev.structured_data, pathways_module: moduleOverride }
        }));
      } else {
        setFormData(prev => ({ ...prev, content: rawContent }));
      }
      toast.info('Template loaded. Select a case to populate participant data.');
    }
  };
  
  const handleModuleConfirm = async () => {
    if (!selectedModule) {
      toast.error('Please select a module');
      return;
    }
    setShowModuleDialog(false);
    await applyTemplate(pendingTemplateId, selectedModule);
    setPendingTemplateId(null);
  };
  
  const handleModuleCancel = () => {
    setShowModuleDialog(false);
    setPendingTemplateId(null);
    setSelectedModule('');
  };
  
  // Supportive Service Type Selection handlers
  const handleServiceTypeConfirm = async () => {
    if (!selectedServiceType) {
      toast.error('Please select a supportive service type');
      return;
    }
    setShowServiceTypeDialog(false);
    
    // Find the selected child template
    const childTemplate = supportiveServiceTypes.find(t => t.service_type === selectedServiceType);
    
    if (childTemplate) {
      // Check if participant is in retention by looking at the case's targeted_outcomes_data
      const caseId = formData.case_id || selectedCase?.id;
      let isInRetention = false;
      
      if (caseId) {
        try {
          // Fetch the case's targeted outcomes to check retention status
          const caseResponse = await axios.get(`${API}/cases/${caseId}/targeted-outcomes`);
          isInRetention = caseResponse.data?.targeted_outcomes?.in_retention === true;
        } catch (error) {
          console.error('Failed to fetch retention status:', error);
          // If we can't fetch, check from selectedCase if available
          isInRetention = selectedCase?.targeted_outcomes_data?.in_retention === true;
        }
      }
      
      // Update the subject to include the service type (with Retention prefix if applicable)
      const subject = isInRetention 
        ? `Retention Supportive Service Assistance - ${selectedServiceType}`
        : `Supportive Service Assistance - ${selectedServiceType}`;
      setFormData(prev => ({ ...prev, subject }));
      
      // Apply the child template's body content
      await applyServiceTypeTemplate(childTemplate, isInRetention);
    }
    
    setPendingTemplateId(null);
  };
  
  const handleServiceTypeCancel = () => {
    setShowServiceTypeDialog(false);
    setPendingTemplateId(null);
    setSelectedServiceType('');
  };
  
  const applyServiceTypeTemplate = async (childTemplate, isInRetention = false) => {
    // Set the parent template as selected but use child template's body
    const parentTemplate = templates.find(t => t.id === 'tpl-supportive-service-assistance');
    setSelectedTemplate(parentTemplate);
    setFormData(prev => ({ 
      ...prev, 
      template_id: 'tpl-supportive-service-assistance',
      structured_data: { 
        ...prev.structured_data, 
        supportive_service_type: childTemplate.service_type,
        is_retention: isInRetention
      }
    }));
    
    // Get the case_id from formData or selectedCase
    const caseId = formData.case_id || selectedCase?.id;
    
    // Modify template body to include Retention prefix in the title if applicable
    let templateBody = childTemplate.body;
    if (isInRetention && templateBody) {
      templateBody = templateBody.replace(
        /^Supportive Service Assistance/,
        'Retention Supportive Service Assistance'
      );
    }
    
    // If we have a case selected, populate merge fields
    if (templateBody && caseId) {
      try {
        const response = await axios.post(`${API}/case-note-templates/populate`, {
          case_id: caseId,
          body: templateBody
        });
        
        if (response.data.populated_body) {
          setFormData(prev => ({ ...prev, content: response.data.populated_body }));
          toast.success(isInRetention 
            ? 'Retention template loaded with participant data' 
            : 'Template loaded with participant data'
          );
        }
      } catch (error) {
        console.error('Failed to populate template:', error);
        // Fall back to raw template body
        setFormData(prev => ({ ...prev, content: templateBody }));
        toast.info('Template loaded (merge fields not populated)');
      }
    } else if (templateBody) {
      // No case selected yet, just load the raw template
      setFormData(prev => ({ ...prev, content: templateBody }));
      toast.info('Template loaded. Select a case to populate participant data.');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAiAssist = async () => {
    if (!formData.case_id) {
      toast.error('Please select a case first');
      return;
    }
    if (!aiInput.trim()) {
      toast.error('Please describe what you want to document');
      return;
    }

    setAiAssisting(true);
    try {
      const response = await axios.post(`${API}/ai/case-note-assist`, {
        case_id: formData.case_id,
        template_id: formData.template_id || null,
        note_type: formData.note_type,
        user_input: aiInput,
        context: {}
      });
      
      setAiDraft(response.data.draft);
      setAiResponse(response.data); // Store full response for follow-up questions
      
      if (response.data.follow_up_questions && response.data.follow_up_questions.length > 0) {
        toast.success('AI draft generated. Please review and provide additional information for highlighted sections.');
      } else {
        toast.success('AI draft generated. Please review and edit before saving.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'AI assistance failed');
    } finally {
      setAiAssisting(false);
    }
  };

  const applyAiDraft = () => {
    setFormData(prev => ({ ...prev, content: aiDraft }));
    setAiDraft('');
    setAiResponse(null);
    toast.info('Draft applied. Please review and make any necessary edits.');
  };
  
  // Document upload handlers
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Add new files
    setUploadedFiles(prev => [...prev, ...files]);
    
    // Initialize document types and names for new files
    const newTypes = files.map(() => 'Other');
    const newNames = files.map(f => f.name.replace(/\.[^/.]+$/, "")); // Remove extension
    
    setDocumentTypes(prev => [...prev, ...newTypes]);
    setDocumentNames(prev => [...prev, ...newNames]);
    
    toast.success(`${files.length} document(s) added`);
    
    // Reset the input so the same file can be selected again if removed
    e.target.value = '';
  };
  
  const handleRemoveFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    setDocumentTypes(prev => prev.filter((_, i) => i !== index));
    setDocumentNames(prev => prev.filter((_, i) => i !== index));
  };
  
  const updateDocumentType = (index, type) => {
    setDocumentTypes(prev => {
      const updated = [...prev];
      updated[index] = type;
      return updated;
    });
  };
  
  const updateDocumentName = (index, name) => {
    setDocumentNames(prev => {
      const updated = [...prev];
      updated[index] = name;
      return updated;
    });
  };
  
  const handleRedirectToTargetedOutcomes = () => {
    // Navigate to the case page with the targeted outcomes tab/card highlighted
    navigate(`/cases/${formData.case_id}`, { 
      state: { showTargetedOutcomes: true, highlightTargetedOutcomes: true }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.case_id) {
      toast.error('Please select a case');
      return;
    }

    if (!formData.subject.trim()) {
      toast.error('Please enter a subject line');
      return;
    }

    if (!formData.content.trim()) {
      toast.error('Please enter note content');
      return;
    }
    
    // Check targeted outcomes for SNAP E&T cases (unless user can bypass)
    if (targetedOutcomesStatus && 
        targetedOutcomesStatus.is_snap_et_case && 
        !targetedOutcomesStatus.is_complete && 
        !targetedOutcomesStatus.can_bypass) {
      setShowTargetedOutcomesAlert(true);
      toast.error('Please complete the Targeted Outcomes card before adding a case note');
      return;
    }

    // Check for contradiction with highlighted/pinned notes (for new notes only)
    if (!isEditMode) {
      setCheckingContradiction(true);
      try {
        const contradictionResponse = await axios.post(`${API}/case-notes/check-contradiction`, {
          case_id: formData.case_id,
          content: formData.content
        });
        
        if (contradictionResponse.data.has_contradiction) {
          setContradictionResult(contradictionResponse.data);
          setShowContradictionAlert(true);
          setCheckingContradiction(false);
          return; // Block save - user must resolve the conflict
        }
        
        // If AI check failed but didn't find contradiction, show warning but allow save
        if (contradictionResponse.data.ai_check_failed) {
          toast.warning('AI contradiction check could not be performed. Please manually review any highlighted notes before saving.');
        }
      } catch (error) {
        console.error('Contradiction check failed:', error);
        // On network error, warn but allow save
        toast.warning('Could not check for conflicts with highlighted notes. Please review manually.');
      } finally {
        setCheckingContradiction(false);
      }
    }

    // Check for employment language in the note (non-blocking - just show alert)
    let employmentAnalysis = null;
    try {
      const analysisResponse = await axios.post(`${API}/cases/${formData.case_id}/analyze-note-for-employment`, {
        note_text: formData.content
      });
      employmentAnalysis = analysisResponse.data;
    } catch (error) {
      console.log('Employment analysis not available:', error);
    }

    setSaving(true);
    try {
      if (isEditMode) {
        // Update existing note
        await axios.put(`${API}/case-notes/${noteId}`, {
          subject: formData.subject,
          content: formData.content,
          note_type: formData.note_type,
          date_of_service: formData.date_of_service,
          is_sensitive: formData.is_sensitive,
          structured_data: formData.structured_data
        });
        toast.success('Note updated successfully');
      } else if (uploadedFiles.length > 0) {
        // Create new note with documents using FormData
        const submitFormData = new FormData();
        submitFormData.append('case_id', formData.case_id);
        submitFormData.append('subject', formData.subject);
        submitFormData.append('date_of_service', formData.date_of_service);
        submitFormData.append('content', formData.content);
        submitFormData.append('note_type', formData.note_type);
        submitFormData.append('is_sensitive', formData.is_sensitive);
        submitFormData.append('template_id', formData.template_id || '');
        submitFormData.append('structured_data', JSON.stringify(formData.structured_data || {}));
        submitFormData.append('document_types', JSON.stringify(documentTypes));
        submitFormData.append('document_names', JSON.stringify(documentNames));
        
        // Append files
        uploadedFiles.forEach((file) => {
          submitFormData.append('files', file);
        });
        
        const response = await axios.post(`${API}/case-notes/with-documents`, submitFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        const docCount = response.data.uploaded_documents?.length || 0;
        toast.success(`Note created successfully${docCount > 0 ? ` with ${docCount} document(s)` : ''}`);
        await sendSuccessCoachingMessages(response.data.note?.id);
      } else {
        // Create new note without documents
        const createResponse = await axios.post(`${API}/case-notes`, formData);
        toast.success('Note created successfully');
        await sendSuccessCoachingMessages(createResponse.data?.id);
      }
      
      // Show employment outcome reminder if employment language was detected
      if (employmentAnalysis && employmentAnalysis.show_alert) {
        setEmploymentAlertMessage(employmentAnalysis.message || 'This case note appears to contain employment-related information. Please consider completing the Participant Employment Outcome card.');
        setShowEmploymentOutcomeAlert(true);
        // Don't navigate immediately - let user see the alert and decide
        return;
      }
      
      // Navigate back to the Case View page to preserve workflow context
      navigate(`/cases/${formData.case_id}`, { 
        state: { activeTab: 'notes', scrollToNotes: true }
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isEditMode ? 'update' : 'create'} note`);
    } finally {
      setSaving(false);
    }
  };

  // Handler for dismissing employment alert and navigating back
  const handleDismissEmploymentAlert = () => {
    setShowEmploymentOutcomeAlert(false);
    navigate(`/cases/${formData.case_id}`, { 
      state: { activeTab: 'notes', scrollToNotes: true }
    });
  };

  // Handler for going to complete the employment outcome card
  const handleGoToEmploymentOutcome = () => {
    setShowEmploymentOutcomeAlert(false);
    navigate(`/cases/${formData.case_id}`, { 
      state: { activeTab: 'overview', highlightEmploymentOutcome: true }
    });
  };
  
  // Check if content contains {{enrollment_summary}} placeholder and show popup if needed
  const checkForEnrollmentSummaryPlaceholder = (content) => {
    if (content && (content.includes('{{enrollment_summary}}') || content.includes('{enrollment_summary}'))) {
      setPendingContentWithEnrollmentSummary(content);
      setEnrollmentSummaryData({
        who_lives_in_household: '',
        household_income_source: '',
        has_criminal_background: false,
        criminal_background_details: ''
      });
      setShowEnrollmentSummaryPopup(true);
      return true;
    }
    return false;
  };
  
  // Handle enrollment summary popup form submission
  const handleEnrollmentSummarySubmit = async () => {
    if (!enrollmentSummaryData.who_lives_in_household.trim()) {
      toast.error('Please describe who lives in the household');
      return;
    }
    if (!enrollmentSummaryData.household_income_source.trim()) {
      toast.error('Please describe the household income source');
      return;
    }
    if (enrollmentSummaryData.has_criminal_background && !enrollmentSummaryData.criminal_background_details.trim()) {
      toast.error('Please provide criminal background details');
      return;
    }
    
    setEnrollmentSummaryLoading(true);
    try {
      const response = await axios.post(`${API}/case-notes/generate-enrollment-summary`, {
        case_id: formData.case_id,
        participant_id: selectedCase?.participant_id,
        who_lives_in_household: enrollmentSummaryData.who_lives_in_household,
        household_income_source: enrollmentSummaryData.household_income_source,
        has_criminal_background: enrollmentSummaryData.has_criminal_background,
        criminal_background_details: enrollmentSummaryData.criminal_background_details
      });
      
      if (response.data.success && response.data.enrollment_summary) {
        // Replace the placeholder in the content
        let updatedContent = pendingContentWithEnrollmentSummary
          .replace('{{enrollment_summary}}', response.data.enrollment_summary)
          .replace('{enrollment_summary}', response.data.enrollment_summary);
        
        setFormData(prev => ({ ...prev, content: updatedContent }));
        setShowEnrollmentSummaryPopup(false);
        setPendingContentWithEnrollmentSummary('');
        toast.success('Enrollment summary generated and inserted');
      } else {
        toast.error('Failed to generate enrollment summary');
      }
    } catch (error) {
      console.error('Failed to generate enrollment summary:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate enrollment summary');
    } finally {
      setEnrollmentSummaryLoading(false);
    }
  };
  
  // Handle closing the enrollment summary popup without generating
  const handleEnrollmentSummaryCancel = () => {
    setShowEnrollmentSummaryPopup(false);
    // Keep the placeholder in the content so user can try again later
    if (pendingContentWithEnrollmentSummary) {
      setFormData(prev => ({ ...prev, content: pendingContentWithEnrollmentSummary }));
    }
    setPendingContentWithEnrollmentSummary('');
    toast.info('Enrollment summary placeholder left in note. You can complete it later.');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="case-note-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            {isEditMode ? (
              <>
                <Edit className="w-7 h-7" />
                Edit Case Note
              </>
            ) : (
              'New Case Note'
            )}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isEditMode ? 'Modify the case note details' : 'Document a case interaction'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Targeted Outcomes Alert for SNAP E&T */}
          {showTargetedOutcomesAlert && targetedOutcomesStatus && (
            <Alert variant="destructive" className="border-orange-500 bg-orange-50 dark:bg-orange-900/20" data-testid="targeted-outcomes-alert">
              <Target className="h-5 w-5 text-orange-600" />
              <AlertTitle className="text-orange-800 dark:text-orange-200">
                SNAP E&T Targeted Outcomes Incomplete
              </AlertTitle>
              <AlertDescription className="text-orange-700 dark:text-orange-300">
                <p className="mb-2">
                  Before adding a case note, you must complete the Targeted Outcomes card for this SNAP E&T case. 
                  The following fields are required:
                </p>
                <ul className="list-disc list-inside mb-3 text-sm">
                  {targetedOutcomesStatus.missing_fields.map((field) => (
                    <li key={field} className="capitalize">{field.replace(/_/g, ' ')}</li>
                  ))}
                </ul>
                <Button 
                  type="button"
                  variant="outline" 
                  size="sm"
                  className="border-orange-500 text-orange-700 hover:bg-orange-100"
                  onClick={handleRedirectToTargetedOutcomes}
                  data-testid="go-to-targeted-outcomes-btn"
                >
                  <Target className="w-4 h-4 mr-2" />
                  Complete Targeted Outcomes
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* Employment Outcome Reminder Alert - Non-blocking */}
          {showEmploymentOutcomeAlert && (
            <Alert className="border-green-500 bg-green-50 dark:bg-green-900/20" data-testid="employment-outcome-alert">
              <Briefcase className="h-5 w-5 text-green-600" />
              <AlertTitle className="text-green-800 dark:text-green-200">
                Employment Information Detected
              </AlertTitle>
              <AlertDescription className="text-green-700 dark:text-green-300">
                <p className="mb-3">
                  {employmentAlertMessage}
                </p>
                <div className="flex gap-2">
                  <Button 
                    type="button"
                    variant="outline" 
                    size="sm"
                    className="border-green-500 text-green-700 hover:bg-green-100"
                    onClick={handleGoToEmploymentOutcome}
                    data-testid="go-to-employment-outcome-btn"
                  >
                    <Briefcase className="w-4 h-4 mr-2" />
                    Complete Employment Outcome
                  </Button>
                  <Button 
                    type="button"
                    variant="ghost" 
                    size="sm"
                    className="text-green-700 hover:bg-green-100"
                    onClick={handleDismissEmploymentAlert}
                    data-testid="dismiss-employment-alert-btn"
                  >
                    Skip for Now
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}
          
          {/* Contradiction with Highlighted Notes Alert - BLOCKS SAVE */}
          {showContradictionAlert && contradictionResult && (
            <Alert className="border-red-500 bg-red-50 dark:bg-red-900/20" data-testid="contradiction-alert">
              <ShieldAlert className="h-5 w-5 text-red-600" />
              <AlertTitle className="text-red-800 dark:text-red-200 font-bold">
                Conflict with Pinned Alert Detected
              </AlertTitle>
              <AlertDescription className="text-red-700 dark:text-red-300">
                <p className="mb-2 font-medium">
                  Your case note conflicts with one or more highlighted/pinned alerts on this case. The note cannot be saved until this conflict is resolved.
                </p>
                {contradictionResult.conflicting_note_subjects && contradictionResult.conflicting_note_subjects.length > 0 && (
                  <div className="mb-2">
                    <p className="text-sm font-medium">Conflicting alerts:</p>
                    <ul className="list-disc list-inside text-sm ml-2">
                      {contradictionResult.conflicting_note_subjects.map((subject, idx) => (
                        <li key={idx}>{subject}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="mb-2 text-sm bg-red-100 p-2 rounded">
                  <strong>Conflict explanation:</strong> {contradictionResult.explanation}
                </p>
                {contradictionResult.recommended_action && (
                  <p className="mb-3 text-sm italic">
                    <strong>Recommended action:</strong> {contradictionResult.recommended_action}
                  </p>
                )}
                <div className="flex gap-2">
                  <Button 
                    type="button"
                    variant="outline" 
                    size="sm"
                    className="border-red-500 text-red-700 hover:bg-red-100"
                    onClick={() => setShowContradictionAlert(false)}
                    data-testid="edit-note-btn"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit My Note
                  </Button>
                  <Button 
                    type="button"
                    variant="ghost" 
                    size="sm"
                    className="text-red-700 hover:bg-red-100"
                    onClick={() => navigate(`/cases/${formData.case_id}`, { state: { activeTab: 'notes' } })}
                    data-testid="view-pinned-notes-btn"
                  >
                    View Pinned Alerts
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Case Selection */}
            <Card className="card-shadow">
              <CardHeader>
                <CardTitle>Case</CardTitle>
                <CardDescription>
                  {isEditMode 
                    ? 'This note is associated with the case below' 
                    : participant 
                      ? `Select a case for ${participant.full_name}`
                      : 'Select the case for this note'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {selectedCase ? (
                  <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                    <div>
                      <p className="font-medium">{selectedCase.participant_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {selectedCase.id} • <span className="capitalize">{selectedCase.service_type_name || selectedCase.case_type?.replace(/_/g, ' ')}</span>
                      </p>
                    </div>
                    {!isEditMode && !preselectedCase && (
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setSelectedCase(null);
                          setFormData(prev => ({ ...prev, case_id: '' }));
                        }}
                      >
                        Change
                      </Button>
                    )}
                  </div>
                ) : preselectedParticipant && participantCases.length > 0 ? (
                  // Dropdown for participant's cases
                  <div className="space-y-4">
                    {participant && (
                      <div className="p-3 bg-muted/50 rounded-lg text-sm">
                        <p className="font-medium">{participant.full_name}</p>
                        <p className="text-muted-foreground">ID: {participant.id}</p>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Select Case *</Label>
                      <Select
                        value={formData.case_id}
                        onValueChange={async (value) => {
                          const caseItem = participantCases.find(c => c.id === value);
                          if (caseItem) {
                            setSelectedCase(caseItem);
                            setFormData(prev => ({ ...prev, case_id: value }));
                            
                            // Check targeted outcomes status for this case
                            await checkTargetedOutcomesStatus(value);
                            
                            // If a template with body is already selected, populate merge fields
                            if (selectedTemplate && selectedTemplate.body) {
                              try {
                                const response = await axios.post(`${API}/case-note-templates/populate`, {
                                  case_id: value,
                                  body: selectedTemplate.body
                                });
                                
                                if (response.data.populated_body) {
                                  setFormData(prev => ({ ...prev, content: response.data.populated_body }));
                                  toast.success('Template updated with participant data');
                                }
                              } catch (error) {
                                console.error('Failed to populate template:', error);
                              }
                            }
                          }
                        }}
                      >
                        <SelectTrigger data-testid="case-select">
                          <SelectValue placeholder="Select a case..." />
                        </SelectTrigger>
                        <SelectContent>
                          {participantCases.map((caseItem) => (
                            <SelectItem key={caseItem.id} value={caseItem.id}>
                              <div className="flex flex-col">
                                <span className="font-medium capitalize">
                                  {caseItem.service_type_name || caseItem.case_type?.replace(/_/g, ' ')}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {caseItem.id} • {caseItem.program_name || 'No program'}
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {participantCases.length === 0 && (
                      <p className="text-sm text-muted-foreground">No open cases found for this participant.</p>
                    )}
                  </div>
                ) : preselectedParticipant && participantCases.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground">
                    <p>No open cases found for this participant.</p>
                    <p className="text-sm mt-1">Please create a case first before adding notes.</p>
                  </div>
                ) : (
                  // Search for cases (default behavior)
                  <div className="space-y-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search cases by ID or participant name..."
                        value={caseSearch}
                        onChange={(e) => {
                          setCaseSearch(e.target.value);
                          searchCases(e.target.value);
                        }}
                        className="pl-9"
                        data-testid="case-search-input"
                      />
                    </div>
                    {cases.length > 0 && (
                      <div className="border border-border rounded-lg divide-y divide-border max-h-60 overflow-y-auto">
                        {cases.map((caseItem) => (
                          <button
                            key={caseItem.id}
                            type="button"
                            className="w-full p-3 text-left hover:bg-muted transition-colors"
                            onClick={() => handleCaseSelect(caseItem)}
                          >
                            <p className="font-medium">{caseItem.participant_name}</p>
                            <p className="text-sm text-muted-foreground">{caseItem.id} • {caseItem.case_type}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Note Details */}
            <Card className="card-shadow">
              <CardHeader>
                <CardTitle>Note Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Template Selection - First */}
                <div className="space-y-2">
                  <Label htmlFor="template_id">Template (Optional)</Label>
                  <Select 
                    value={formData.template_id || "none"} 
                    onValueChange={(value) => handleTemplateSelect(value === "none" ? "" : value)}
                  >
                    <SelectTrigger data-testid="template-select">
                      <SelectValue placeholder="Select a template" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Template</SelectItem>
                      {getFilteredTemplates().map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Select a template to pre-fill the note content
                    {selectedCase && getFilteredTemplates().length < templates.length && (
                      <span className="ml-1 text-blue-600">
                        (Showing {getFilteredTemplates().length} of {templates.length} templates for this case type)
                      </span>
                    )}
                  </p>
                </div>

                {/* Subject Line - Required */}
                <div className="space-y-2">
                  <Label htmlFor="subject">Subject Line *</Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="e.g., Weekly check-in, Job placement follow-up, Crisis intervention"
                    required
                    data-testid="subject-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    A brief summary of this note (required for all notes)
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="date_of_service">Date of Service *</Label>
                    <Input
                      id="date_of_service"
                      name="date_of_service"
                      type="date"
                      value={formData.date_of_service}
                      onChange={handleChange}
                      required
                      data-testid="date-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="note_type">Note Type</Label>
                    <Select 
                      value={formData.note_type} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, note_type: value }))}
                    >
                      <SelectTrigger data-testid="note-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {noteTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="content">Note Content *</Label>
                  <Textarea
                    id="content"
                    name="content"
                    value={formData.content}
                    onChange={handleChange}
                    placeholder="Document the interaction, services provided, and outcomes..."
                    rows={12}
                    required
                    data-testid="content-textarea"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="is_sensitive"
                      checked={formData.is_sensitive}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_sensitive: checked }))}
                    />
                    <Label htmlFor="is_sensitive" className="text-sm">
                      Mark as sensitive (restricted visibility)
                    </Label>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Document Upload Section - Only for new notes */}
            {!isEditMode && (
              <Card className="card-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Upload className="w-5 h-5" />
                    Attach Documents
                  </CardTitle>
                  <CardDescription>
                    Upload documents to be saved with this note and to the participant's case documents
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* File Input */}
                  <div className="flex items-center gap-4">
                    <input
                      type="file"
                      id="document-upload"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt,.csv"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('document-upload').click()}
                      data-testid="upload-document-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Select Files
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Supported: PDF, Word, Excel, Images, Text
                    </span>
                  </div>
                  
                  {/* Uploaded Files List */}
                  {uploadedFiles.length > 0 && (
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">Documents to Upload ({uploadedFiles.length})</Label>
                      <div className="space-y-2">
                        {uploadedFiles.map((file, index) => (
                          <div key={index} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                            <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
                              <Input
                                value={documentNames[index] || ''}
                                onChange={(e) => updateDocumentName(index, e.target.value)}
                                placeholder="Document name"
                                className="h-8"
                                data-testid={`doc-name-${index}`}
                              />
                              <Select
                                value={documentTypes[index] || 'Other'}
                                onValueChange={(value) => updateDocumentType(index, value)}
                              >
                                <SelectTrigger className="h-8" data-testid={`doc-type-${index}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {DOCUMENT_TYPE_OPTIONS.map((type) => (
                                    <SelectItem key={type} value={type}>{type}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground truncate flex-1">
                                  {file.name} ({(file.size / 1024).toFixed(1)} KB)
                                </span>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveFile(index)}
                                  className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                  data-testid={`remove-doc-${index}`}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Documents will be automatically saved to the participant's case documents.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-4">
              <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={saving || checkingContradiction || showContradictionAlert || !formData.case_id || !formData.content || !formData.subject} 
                data-testid="save-btn"
              >
                {checkingContradiction ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Checking for conflicts...
                  </>
                ) : saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {isEditMode ? 'Updating...' : 'Saving...'}
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {isEditMode ? 'Update Note' : 'Save as Draft'}
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>

        {/* AI Assistant Panel */}
        <div className="space-y-6">
          <Card className="card-shadow sticky top-24">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-accent" />
                AI Note Assistant
              </CardTitle>
              <CardDescription>
                {selectedTemplate 
                  ? `Using template: ${selectedTemplate.name}`
                  : 'Get help drafting your case note'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <div className="flex gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5" />
                  <p className="text-xs text-amber-800 dark:text-amber-200">
                    AI uses participant profile data to help draft notes. All content must be verified before submission.
                  </p>
                </div>
              </div>

              {/* Template info if selected */}
              {selectedTemplate && (
                <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
                  <p className="text-sm font-medium text-primary mb-1">Template Selected</p>
                  <p className="text-xs text-muted-foreground">
                    The AI will follow the structure of "{selectedTemplate.name}" and pull relevant data from the participant's profile.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="ai-input">
                  {selectedTemplate 
                    ? 'Describe the interaction (AI will use template structure)'
                    : 'Describe the interaction'}
                </Label>
                <Textarea
                  id="ai-input"
                  value={aiInput}
                  onChange={(e) => setAiInput(e.target.value)}
                  placeholder={selectedTemplate 
                    ? `Describe the interaction for your ${selectedTemplate.name}. The AI will structure it according to the template and include relevant participant data...`
                    : "E.g., Met with client for 30 minutes to discuss job search progress. They completed two applications this week..."}
                  rows={4}
                  data-testid="ai-input"
                />
              </div>

              <Button 
                type="button"
                className="w-full"
                onClick={handleAiAssist}
                disabled={aiAssisting || !formData.case_id}
                data-testid="ai-assist-btn"
              >
                {aiAssisting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating{selectedTemplate ? ' from Template...' : '...'}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    {selectedTemplate ? 'Generate from Template' : 'Generate Draft'}
                  </>
                )}
              </Button>

              {aiDraft && (
                <div className="space-y-3 pt-4 border-t border-border">
                  <div className="flex items-center justify-between">
                    <Label>AI Generated Draft</Label>
                    {aiResponse?.participant_data_used && (
                      <Badge variant="outline" className="text-[10px]">
                        Profile data included
                      </Badge>
                    )}
                  </div>
                  <div className="p-3 bg-muted rounded-lg text-sm max-h-64 overflow-y-auto">
                    <pre className="whitespace-pre-wrap font-sans">{aiDraft}</pre>
                  </div>
                  
                  {/* Follow-up questions from template */}
                  {aiResponse?.follow_up_questions && aiResponse.follow_up_questions.length > 0 && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <p className="text-xs font-medium text-blue-800 dark:text-blue-200 mb-2">
                        Additional information needed:
                      </p>
                      <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                        {aiResponse.follow_up_questions.map((q, i) => (
                          <li key={i} className="flex items-start gap-1">
                            <span>•</span>
                            <span>{q}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <Button 
                    type="button" 
                    variant="secondary" 
                    className="w-full"
                    onClick={applyAiDraft}
                    data-testid="apply-draft-btn"
                  >
                    Apply to Note
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Pathways Module Selection Dialog */}
      <Dialog open={showModuleDialog} onOpenChange={setShowModuleDialog}>
        <DialogContent className="sm:max-w-md" data-testid="module-selection-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-purple-600" />
              Select Completed Module
            </DialogTitle>
            <DialogDescription>
              Choose which Pathways to Employability module the participant has completed.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedModule} onValueChange={setSelectedModule}>
              <SelectTrigger data-testid="module-select">
                <SelectValue placeholder="Select a module..." />
              </SelectTrigger>
              <SelectContent>
                {PATHWAYS_MODULES.map((module) => (
                  <SelectItem key={module} value={module} data-testid={`module-option-${module.replace(/\s+/g, '-').toLowerCase()}`}>
                    {module}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={handleModuleCancel} data-testid="module-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleModuleConfirm} disabled={!selectedModule} data-testid="module-confirm-btn">
              Confirm & Load Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Supportive Service Type Selection Dialog */}
      <Dialog open={showServiceTypeDialog} onOpenChange={setShowServiceTypeDialog}>
        <DialogContent className="sm:max-w-md" data-testid="service-type-selection-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-green-600" />
              Select Supportive Service Type
            </DialogTitle>
            <DialogDescription>
              Choose the type of supportive service assistance being provided.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedServiceType} onValueChange={setSelectedServiceType}>
              <SelectTrigger data-testid="service-type-select">
                <SelectValue placeholder="Select a service type..." />
              </SelectTrigger>
              <SelectContent>
                {supportiveServiceTypes.map((service) => (
                  <SelectItem 
                    key={service.id || service.service_type} 
                    value={service.service_type}
                    data-testid={`service-type-option-${service.service_type.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {service.service_type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={handleServiceTypeCancel} data-testid="service-type-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleServiceTypeConfirm} disabled={!selectedServiceType} data-testid="service-type-confirm-btn">
              Confirm & Load Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <SuccessCoachingQuestionsDialog
        open={showScDialog}
        caseId={formData.case_id}
        weekInfo={scWeekInfo}
        onComplete={handleScComplete}
        onSkip={handleScSkip}
      />

      {/* Enrollment Summary Popup Dialog - for {{enrollment_summary}} merge field */}
      <Dialog open={showEnrollmentSummaryPopup} onOpenChange={(open) => !open && handleEnrollmentSummaryCancel()}>
        <DialogContent className="sm:max-w-lg" data-testid="enrollment-summary-popup">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Enrollment Summary Information
            </DialogTitle>
            <DialogDescription>
              Please provide the following information to generate an AI-powered enrollment summary for this case note.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Who lives in household */}
            <div className="space-y-2">
              <Label htmlFor="who_lives_in_household" className="flex items-center gap-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                Who lives in the participant's household? *
              </Label>
              <Textarea
                id="who_lives_in_household"
                value={enrollmentSummaryData.who_lives_in_household}
                onChange={(e) => setEnrollmentSummaryData(prev => ({ ...prev, who_lives_in_household: e.target.value }))}
                placeholder="e.g., Participant lives alone, or Participant lives with spouse and two children (ages 5 and 8)..."
                rows={2}
                data-testid="household-input"
              />
            </div>
            
            {/* Household income source */}
            <div className="space-y-2">
              <Label htmlFor="household_income_source" className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-muted-foreground" />
                What is the household's source of income? *
              </Label>
              <Textarea
                id="household_income_source"
                value={enrollmentSummaryData.household_income_source}
                onChange={(e) => setEnrollmentSummaryData(prev => ({ ...prev, household_income_source: e.target.value }))}
                placeholder="e.g., SNAP benefits, part-time employment at local grocery store, unemployment benefits..."
                rows={2}
                data-testid="income-source-input"
              />
            </div>
            
            {/* Criminal background checkbox */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="has_criminal_background"
                  checked={enrollmentSummaryData.has_criminal_background}
                  onCheckedChange={(checked) => setEnrollmentSummaryData(prev => ({ 
                    ...prev, 
                    has_criminal_background: checked,
                    criminal_background_details: checked ? prev.criminal_background_details : ''
                  }))}
                  data-testid="criminal-background-checkbox"
                />
                <Label htmlFor="has_criminal_background" className="flex items-center gap-2 cursor-pointer">
                  <ShieldAlert className="w-4 h-4 text-muted-foreground" />
                  Participant has disclosed a criminal background
                </Label>
              </div>
              
              {/* Criminal background details - shown only if checkbox is checked */}
              {enrollmentSummaryData.has_criminal_background && (
                <div className="space-y-2 ml-6">
                  <Label htmlFor="criminal_background_details">
                    Please provide relevant details *
                  </Label>
                  <Textarea
                    id="criminal_background_details"
                    value={enrollmentSummaryData.criminal_background_details}
                    onChange={(e) => setEnrollmentSummaryData(prev => ({ ...prev, criminal_background_details: e.target.value }))}
                    placeholder="e.g., Misdemeanor charge from 5 years ago, completed probation. Participant has disclosed this during intake..."
                    rows={2}
                    data-testid="criminal-details-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    This information will be incorporated into the enrollment summary as a barrier to employment.
                  </p>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={handleEnrollmentSummaryCancel} 
              disabled={enrollmentSummaryLoading}
              data-testid="enrollment-summary-cancel-btn"
            >
              Skip for Now
            </Button>
            <Button 
              onClick={handleEnrollmentSummarySubmit} 
              disabled={enrollmentSummaryLoading || !enrollmentSummaryData.who_lives_in_household.trim() || !enrollmentSummaryData.household_income_source.trim()}
              data-testid="enrollment-summary-submit-btn"
            >
              {enrollmentSummaryLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Summary
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CaseNoteFormPage;
